import { Injectable, Injector } from '@angular/core';
import { ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import { CrashReportService } from '../crash-report.service';
import { Config } from './Config';
import { Log } from '../helpers/Log';
import { StorageHelper } from '../helpers/StorageHelper';
import { ContextInfoHelper } from '../helpers/ContextInfoHelper';

@Injectable()
export class MyErrorHandler implements ErrorHandler {

  private router: Router;

  constructor(
    private crashReportService: CrashReportService,
    private injector: Injector
    ) {

  }

  private getRouter(): Router {
    if (!this.router) {
      try {
        this.router = this.injector.get(Router);
      } catch (e) {
        // Router not available yet
        return null;
      }
    }
    return this.router;
  }

  private isExternalEventListenerHookError(errorText: string): boolean {
    if (!errorText) {
      return false;
    }

    return errorText.includes("Maximum call stack size exceeded") &&
      errorText.includes("addEL_hook") &&
      errorText.includes("top.addEventListener");
  }

  handleError(error: any) {

    // Ignore HTTP errors with status 0 (network errors, CORS, timeouts)
    // These are not application crashes but network connectivity issues
    if (error && error.status === 0) {
      Log.getInstance().d("Ignoring network error (status 0): " + (error.message || String(error)));
      return; // Don't treat network errors as crashes
    }

    // Ignore HttpErrorResponse with status 0
    if (error && error.name === "HttpErrorResponse" && error.status === 0) {
      Log.getInstance().d("Ignoring HTTP network error (status 0): " + (error.message || String(error)));
      return; // Don't treat network errors as crashes
    }

    // Ignore errors from browser extensions/user scripts
    // These are not application crashes but external script errors
    const errorString = error ? (error.message || error.stack || String(error)) : "";
    if (errorString && (
      errorString.includes("user-script") ||
      errorString.includes("@user-script") ||
      errorString.includes("moz-extension://") ||
      errorString.includes("chrome-extension://") ||
      errorString.includes("safari-extension://") ||
      errorString.includes("extension://") ||
      this.isExternalEventListenerHookError(errorString)
    )) {
      Log.getInstance().d("Ignoring browser extension error: " + errorString);
      return; // Don't treat browser extension errors as crashes
    }

    // Check stack trace for user-script references
    if (error && error.stack && (
      error.stack.includes("user-script") ||
      error.stack.includes("@user-script") ||
      error.stack.includes("moz-extension://") ||
      error.stack.includes("chrome-extension://") ||
      error.stack.includes("safari-extension://") ||
      error.stack.includes("extension://") ||
      this.isExternalEventListenerHookError(error.stack)
    )) {
      Log.getInstance().d("Ignoring browser extension error (from stack): " + (error.message || String(error)));
      return; // Don't treat browser extension errors as crashes
    }

    // Ignore errors related to TrackerStorageType (likely from external tracking libraries or browser extensions)
    // These are not application crashes but external script errors
    if (errorString && (
      errorString.includes("TrackerStorageType") ||
      errorString.includes("TrackerStorageType is not defined")
    )) {
      Log.getInstance().d("Ignoring TrackerStorageType error (external script): " + errorString);
      return; // Don't treat external tracking library errors as crashes
    }

    // Ignore Firefox "can't access dead object" - occurs when DOM is torn down (tab close, navigate away)
    // while Angular change detection tries to update views (e.g. *ngFor). Race condition, not fixable in app.
    if (errorString && errorString.toLowerCase().includes("can't access dead object")) {
      Log.getInstance().d("Ignoring Firefox dead object error (DOM teardown race): " + errorString);
      return;
    }

    if (Config.isProd)
    {
      this.sendToServer(error);
    }
    else
    {
      //this.sendToServer(error);
      this.sendToConsole(error);
    }

  }

  sendToConsole(error: any){
    Log.getInstance().e(error);
    //window.open(Config.frontendUrl + "/error", "_self");
  }

  sendToServer(error: any) {

    let errorMessage = "";
    let errorStack = "";

    if (error) {
      // Extract error message
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else {
        errorMessage = String(error);
      }

      // Extract full stack trace - check multiple possible locations
      if (error.stack) {
        errorStack = error.stack;
      } else if (error.error && error.error.stack) {
        errorStack = error.error.stack;
      } else if (error.rejection && error.rejection.stack) {
        errorStack = error.rejection.stack;
      }

      // Log error message
      Log.getInstance().d(errorMessage);

      // Log full stack trace separately for better visibility
      if (errorStack) {
        Log.getInstance().d("STACK TRACE:\n" + errorStack);
      } else {
        // If no stack trace found, log the full error object for debugging
        // Pass error object directly to Log.d() which handles circular references safely
        Log.getInstance().d("ERROR OBJECT (no stack):");
        Log.getInstance().d(error);
      }
    }

    const logHistory = Log.getInstance().getLogHistory();

    //limit the amount of crash sent to 1 per minute
    const now = new Date().getTime();
    const lastSent = StorageHelper.getInstance().get("crashTimestamp");
    const shouldSend = !lastSent || (now - lastSent > 1000 * 60);

    // Always collect context information when an error occurs
    const router = this.getRouter();
    const contextInfo = ContextInfoHelper.collectContextInfo(router);

    if (logHistory && logHistory.length > 0 && shouldSend) {
      StorageHelper.getInstance().save("crashTimestamp", now);

      // Send error with log history and context information
      this.crashReportService.sendError(logHistory, errorStack || errorMessage, contextInfo).subscribe(
        (res) => {
          window.open(Config.frontendUrl + "/error", "_self");
        }, (err) => {
          window.open(Config.frontendUrl + "/error", "_self");
      });
    }
    else {
      // Even if no log history, send error message with context information
      if (shouldSend && (errorStack || errorMessage)) {
        StorageHelper.getInstance().save("crashTimestamp", now);
        const minimalLog = [errorMessage || "Unknown error occurred"];
        this.crashReportService.sendError(minimalLog, errorStack || errorMessage, contextInfo).subscribe(
          (res) => {
            window.open(Config.frontendUrl + "/error", "_self");
          }, (err) => {
            window.open(Config.frontendUrl + "/error", "_self");
          });
      } else {
        window.open(Config.frontendUrl + "/error", "_self");
      }
    }

  }

}
